/**
 * Capitalize the first letter of each word in a name.
 *
 * A word that already mixes upper and lower case was capitalized on purpose,
 * so only its first letter is normalized. Anything written entirely in one
 * case carries no such intent and is rewritten in full.
 *
 * Examples:
 * - "mary jane" -> "Mary Jane"
 * - "john" -> "John"
 * - "de la cruz" -> "De La Cruz"
 * - "SMITH" -> "Smith"
 * - "McDonald" -> "McDonald"
 * - "o'connor" -> "O'connor"
 */
export function capitalizeName(name: string | null | undefined): string {
  if (!name) {
    return "";
  }

  const trimmed = name.trim();
  if (!trimmed) {
    return "";
  }

  return trimmed
    .split(/\s+/)
    .map((word) => {
      if (word.length === 0) {
        return word;
      }
      const rest =
        word === word.toUpperCase() || word === word.toLowerCase()
          ? word.slice(1).toLowerCase()
          : word.slice(1);
      return word.charAt(0).toUpperCase() + rest;
    })
    .join(" ");
}

/**
 * Check if a user email is from an anonymous or temporary user
 *
 * Anonymous users have email formats like:
 * - anon_1760865781449_j1pegxxvuns@anonymous.terac.local
 * - anon_1760865781449_j1pegxxvuns@anonymous.terac.com
 * - anon_1760865781449_j1pegxxvuns (prefix-only pattern)
 *
 * This function is the single source of truth for anonymous user detection.
 * Use it instead of duplicating these checks across the codebase.
 */
/**
 * Check if a user is a super admin (Terac team member).
 * Super admins have a verified @terac.com email.
 *
 * @deprecated Identity, not authority. The email domain says who someone is; it
 * cannot say what they may do, and a restricted role (see
 * `RESTRICTED_INTERNAL_ROLES`) holds the same domain while being entitled to far
 * less. Server-side authorization reads `ctx.isSuperAdmin`, which is derived
 * from `internalRole` via `hasUnrestrictedInternalAccess`. This remains only for
 * client components that have no role to hand.
 */
export function isSuperAdmin(
  user: { email?: string | null; emailVerified?: boolean } | null | undefined,
): boolean {
  if (!user) {
    return false;
  }
  return (
    (user.email?.endsWith("@terac.com") && user.emailVerified === true) ?? false
  );
}

export function isAnonymousUser(email: string | null | undefined): boolean {
  if (!email) {
    return false;
  }

  const normalizedEmail = email.toLowerCase();

  // Check for anon_ prefix (covers all anonymous user patterns)
  if (normalizedEmail.startsWith("anon_")) {
    return true;
  }

  // Check for anonymous.terac.local or anonymous.terac.com domains
  if (
    normalizedEmail.includes("@anonymous.terac.local") ||
    normalizedEmail.includes("@anonymous.terac.com")
  ) {
    return true;
  }

  // Legacy: check for prolific+ pattern at terac.com (historical anonymous users)
  if (
    normalizedEmail.includes("prolific+") &&
    normalizedEmail.endsWith("@terac.com")
  ) {
    return true;
  }

  return false;
}
